import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '../../core/helpers/analytics.service';
import {
  Chart,
  ChartConfiguration,
  ChartData,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController
} from 'chart.js';
import {
  RegistrationSummary,
  MarketingEmailOptInSummary,
  SlotStatusSummary,
  GamePlaySummary,
  RewardSummary,
  DailyRegistrationTrend,
  DailyMarketingOptInTrend,
  DailySlotStatusTrend,
  DailyGamePlayTrend,
  DailyRewardTrend,
  TimeRange,
  AnalyticsCategory
} from '../../core/models/analytics.models';

// Register Chart.js components
Chart.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  LineController,
  BarController
);

@Component({
  selector: 'loyaltic-analytics',
  standalone: false,
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss'
})
export class AnalyticsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Password authentication
  password: string = '';
  isAuthenticated: boolean = false;
  authError: string = '';

  // Loading states - individual loaders for each section
  isLoadingRegistration = true;
  isLoadingMarketingEmail = true;
  isLoadingSlotStatus = true;
  isLoadingGamePlay = true;
  isLoadingReward = true;

  // Loading states for trends
  isLoadingRegistrationTrend = false;
  isLoadingMarketingEmailTrend = false;
  isLoadingSlotStatusTrend = false;
  isLoadingGamePlayTrend = false;
  isLoadingRewardTrend = false;

  // Summary data
  registrationSummary: RegistrationSummary | null = null;
  marketingEmailSummary: MarketingEmailOptInSummary | null = null;
  slotStatusSummary: SlotStatusSummary | null = null;
  gamePlaySummary: GamePlaySummary | null = null;
  rewardSummary: RewardSummary | null = null;

  // Trend data
  registrationTrendData: DailyRegistrationTrend | null = null;
  marketingEmailTrendData: DailyMarketingOptInTrend | null = null;
  slotStatusTrendData: DailySlotStatusTrend | null = null;
  gamePlayTrendData: DailyGamePlayTrend | null = null;
  rewardTrendData: DailyRewardTrend | null = null;

  // UI State
  selectedTimeRange: TimeRange = 'daily';
  activeTab: AnalyticsCategory = 'registrations';

  // Date range for trends
  startDate: Date | null = null;
  endDate: Date | null = null;

  // Chart configurations
  registrationChartData: ChartData<'line'> = { labels: [], datasets: [] };
  marketingEmailChartData: ChartData<'line'> = { labels: [], datasets: [] };
  slotStatusChartData: ChartData<'bar'> = { labels: [], datasets: [] };
  gamePlayChartData: ChartData<'line'> = { labels: [], datasets: [] };
  rewardChartData: ChartData<'bar'> = { labels: [], datasets: [] };

  // Current chart display
  currentChartData: ChartData<any> = { labels: [], datasets: [] };
  currentChartOptions: ChartConfiguration['options'] = {};
  currentChartType: 'line' | 'bar' = 'line';
  currentStats: { label: string; value: string }[] = [];

  lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        beginAtZero: true
      }
    }
  };

  barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        beginAtZero: true
      }
    }
  };

  constructor(private analyticsService: AnalyticsService) {
    // Set default date range to last 30 days
    this.endDate = new Date();
    this.startDate = new Date('2025-11-16');
    // this.startDate = new Date();
    // this.startDate.setDate(this.startDate.getDate()-37);
  }

  ngOnInit(): void {
    // Data will only load after password authentication
  }

  onPasswordSubmit(): void {
    if (!this.password) {
      this.authError = 'Please enter a password';
      return;
    }

    this.authError = '';
    this.isAuthenticated = true;
    this.loadAllSummaries();
    this.loadTrendData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAllSummaries(): void {
    // Load each summary independently
    this.loadRegistrationSummary();
    this.loadMarketingEmailSummary();
    this.loadSlotStatusSummary();
    this.loadGamePlaySummary();
    this.loadRewardSummary();
  }

  loadRegistrationSummary(): void {
    this.isLoadingRegistration = true;
    this.analyticsService.getRegistrationsSummary(this.password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.registrationSummary = result.result;
          this.isLoadingRegistration = false;
        },
        error: (error) => {
          console.error('Error loading registration summary:', error);
          this.isLoadingRegistration = false;
          this.handleAuthError(error);
        }
      });
  }

  loadMarketingEmailSummary(): void {
    this.isLoadingMarketingEmail = true;
    this.analyticsService.getMarketingEmailsSummary(this.password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.marketingEmailSummary = result.result;
          this.isLoadingMarketingEmail = false;
        },
        error: (error) => {
          console.error('Error loading marketing email summary:', error);
          this.isLoadingMarketingEmail = false;
          this.handleAuthError(error);
        }
      });
  }

  loadSlotStatusSummary(): void {
    this.isLoadingSlotStatus = true;
    this.analyticsService.getSlotsSummary(this.password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.slotStatusSummary = result.result;
          this.isLoadingSlotStatus = false;
        },
        error: (error) => {
          console.error('Error loading slot status summary:', error);
          this.isLoadingSlotStatus = false;
          this.handleAuthError(error);
        }
      });
  }

  loadGamePlaySummary(): void {
    this.isLoadingGamePlay = true;
    this.analyticsService.getGamesSummary(this.password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.gamePlaySummary = result.result;
          this.isLoadingGamePlay = false;
        },
        error: (error) => {
          console.error('Error loading game play summary:', error);
          this.isLoadingGamePlay = false;
          this.handleAuthError(error);
        }
      });
  }

  loadRewardSummary(): void {
    this.isLoadingReward = true;
    this.analyticsService.getRewardsSummary(this.password)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.rewardSummary = result.result;
          this.isLoadingReward = false;
        },
        error: (error) => {
          console.error('Error loading reward summary:', error);
          this.isLoadingReward = false;
          this.handleAuthError(error);
        }
      });
  }

  loadTrendData(): void {
    // Load each trend independently
    this.loadRegistrationTrend();
    this.loadMarketingEmailTrend();
    this.loadSlotStatusTrend();
    this.loadGamePlayTrend();
    this.loadRewardTrend();
  }

  loadRegistrationTrend(): void {
    this.isLoadingRegistrationTrend = true;

    if (this.selectedTimeRange === 'daily') {
      this.analyticsService.getRegistrationsDailyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.registrationTrendData = result.result;
            this.generateRegistrationChart();
            if (this.activeTab === 'registrations') {
              this.updateCurrentChart();
            }
            this.isLoadingRegistrationTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading registration trend:', error);
            this.isLoadingRegistrationTrend = false;
            this.handleAuthError(error);
          }
        });
    } else {
      this.analyticsService.getRegistrationsWeeklyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.registrationTrendData = result.result;
            this.generateRegistrationChart();
            if (this.activeTab === 'registrations') {
              this.updateCurrentChart();
            }
            this.isLoadingRegistrationTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading registration trend:', error);
            this.isLoadingRegistrationTrend = false;
            this.handleAuthError(error);
          }
        });
    }
  }

  loadMarketingEmailTrend(): void {
    this.isLoadingMarketingEmailTrend = true;

    if (this.selectedTimeRange === 'daily') {
      this.analyticsService.getMarketingEmailsDailyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.marketingEmailTrendData = result.result;
            this.generateMarketingEmailChart();
            if (this.activeTab === 'marketing-emails') {
              this.updateCurrentChart();
            }
            this.isLoadingMarketingEmailTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading marketing email trend:', error);
            this.isLoadingMarketingEmailTrend = false;
            this.handleAuthError(error);
          }
        });
    } else {
      this.analyticsService.getMarketingEmailsWeeklyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.marketingEmailTrendData = result.result;
            this.generateMarketingEmailChart();
            if (this.activeTab === 'marketing-emails') {
              this.updateCurrentChart();
            }
            this.isLoadingMarketingEmailTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading marketing email trend:', error);
            this.isLoadingMarketingEmailTrend = false;
            this.handleAuthError(error);
          }
        });
    }
  }

  loadSlotStatusTrend(): void {
    this.isLoadingSlotStatusTrend = true;

    if (this.selectedTimeRange === 'daily') {
      this.analyticsService.getSlotsDailyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.slotStatusTrendData = result.result;
            this.generateSlotStatusChart();
            if (this.activeTab === 'slots') {
              this.updateCurrentChart();
            }
            this.isLoadingSlotStatusTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading slot status trend:', error);
            this.isLoadingSlotStatusTrend = false;
            this.handleAuthError(error);
          }
        });
    } else {
      this.analyticsService.getSlotsWeeklyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.slotStatusTrendData = result.result;
            this.generateSlotStatusChart();
            if (this.activeTab === 'slots') {
              this.updateCurrentChart();
            }
            this.isLoadingSlotStatusTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading slot status trend:', error);
            this.isLoadingSlotStatusTrend = false;
            this.handleAuthError(error);
          }
        });
    }
  }

  loadGamePlayTrend(): void {
    this.isLoadingGamePlayTrend = true;

    if (this.selectedTimeRange === 'daily') {
      this.analyticsService.getGamesDailyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.gamePlayTrendData = result.result;
            this.generateGamePlayChart();
            if (this.activeTab === 'games') {
              this.updateCurrentChart();
            }
            this.isLoadingGamePlayTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading game play trend:', error);
            this.isLoadingGamePlayTrend = false;
            this.handleAuthError(error);
          }
        });
    } else {
      this.analyticsService.getGamesWeeklyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.gamePlayTrendData = result.result;
            this.generateGamePlayChart();
            if (this.activeTab === 'games') {
              this.updateCurrentChart();
            }
            this.isLoadingGamePlayTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading game play trend:', error);
            this.isLoadingGamePlayTrend = false;
            this.handleAuthError(error);
          }
        });
    }
  }

  loadRewardTrend(): void {
    this.isLoadingRewardTrend = true;

    if (this.selectedTimeRange === 'daily') {
      this.analyticsService.getRewardsDailyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.rewardTrendData = result.result;
            this.generateRewardChart();
            if (this.activeTab === 'rewards') {
              this.updateCurrentChart();
            }
            this.isLoadingRewardTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading reward trend:', error);
            this.isLoadingRewardTrend = false;
            this.handleAuthError(error);
          }
        });
    } else {
      this.analyticsService.getRewardsWeeklyTrend(this.password, this.startDate || undefined, this.endDate || undefined)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result: any) => {
            this.rewardTrendData = result.result;
            this.generateRewardChart();
            if (this.activeTab === 'rewards') {
              this.updateCurrentChart();
            }
            this.isLoadingRewardTrend = false;
          },
          error: (error: any) => {
            console.error('Error loading reward trend:', error);
            this.isLoadingRewardTrend = false;
            this.handleAuthError(error);
          }
        });
    }
  }

  generateRegistrationChart(): void {
    if (!this.registrationTrendData) return;

    // Check for weekly or daily data
    const weeklyData = (this.registrationTrendData as any).weeklyRegistrations;
    const dailyData = (this.registrationTrendData as any).dailyRegistrations;
    const isWeekly = !!weeklyData;
    const dataArray = weeklyData || dailyData;

    if (!dataArray) return;

    // Sort by date in ascending order
    const sortedData = [...dataArray].sort((a, b) => {
      const dateA = isWeekly ? new Date(a.weekStartDate).getTime() : new Date(a.date).getTime();
      const dateB = isWeekly ? new Date(b.weekStartDate).getTime() : new Date(b.date).getTime();
      return dateA - dateB;
    });

    const labels = sortedData.map(item =>
      isWeekly ? `Week ${item.weekNumber}, ${item.year}` : this.formatDate(item.date)
    );
    const data = sortedData.map(item => item.count);

    this.registrationChartData = {
      labels,
      datasets: [
        {
          label: 'Registrations',
          data,
          borderColor: '#00823d',
          backgroundColor: 'rgba(0, 130, 61, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  }

  generateMarketingEmailChart(): void {
    if (!this.marketingEmailTrendData) return;

    // Check for weekly or daily data
    const weeklyData = (this.marketingEmailTrendData as any).weeklyOptIns;
    const dailyData = (this.marketingEmailTrendData as any).dailyOptIns;
    const isWeekly = !!weeklyData;
    const dataArray = weeklyData || dailyData;

    if (!dataArray) return;

    // Sort by date in ascending order
    const sortedData = [...dataArray].sort((a, b) => {
      const dateA = isWeekly ? new Date(a.weekStartDate).getTime() : new Date(a.date).getTime();
      const dateB = isWeekly ? new Date(b.weekStartDate).getTime() : new Date(b.date).getTime();
      return dateA - dateB;
    });

    const labels = sortedData.map(item =>
      isWeekly ? `Week ${item.weekNumber}, ${item.year}` : this.formatDate(item.date)
    );
    const data = sortedData.map(item => item.count);

    this.marketingEmailChartData = {
      labels,
      datasets: [
        {
          label: 'Opt-Ins',
          data,
          borderColor: '#cca374',
          backgroundColor: 'rgba(204, 163, 116, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  }

  generateSlotStatusChart(): void {
    if (!this.slotStatusTrendData) return;

    // Check for weekly or daily data
    const weeklyData = (this.slotStatusTrendData as any).weeklySlotStatuses;
    const dailyData = (this.slotStatusTrendData as any).dailySlotStatuses;
    const isWeekly = !!weeklyData;
    const dataArray = weeklyData || dailyData;

    if (!dataArray) return;

    // Sort by date in ascending order
    const sortedData = [...dataArray].sort((a, b) => {
      const dateA = isWeekly ? new Date(a.weekStartDate).getTime() : new Date(a.date).getTime();
      const dateB = isWeekly ? new Date(b.weekStartDate).getTime() : new Date(b.date).getTime();
      return dateA - dateB;
    });

    const labels = sortedData.map(item =>
      isWeekly ? `Week ${item.weekNumber}, ${item.year}` : this.formatDate(item.date)
    );
    // Calculate total slots (sum of all statuses)
    const totalSlotsData = sortedData.map(item =>
      item.pendingCount + item.partialCount + item.completedCount
    );

    this.slotStatusChartData = {
      labels,
      datasets: [
        {
          label: 'Total Slots',
          data: totalSlotsData,
          backgroundColor: '#00823d',
          borderColor: '#00823d',
          borderWidth: 1
        }
      ]
    };
  }

  generateGamePlayChart(): void {
    if (!this.gamePlayTrendData) return;

    // Check for weekly or daily data
    const weeklyData = (this.gamePlayTrendData as any).weeklyGameStats;
    const dailyData = (this.gamePlayTrendData as any).dailyGameStats;
    const isWeekly = !!weeklyData;
    const dataArray = weeklyData || dailyData;

    if (!dataArray) return;

    // Sort by date in ascending order
    const sortedData = [...dataArray].sort((a, b) => {
      const dateA = isWeekly ? new Date(a.weekStartDate).getTime() : new Date(a.date).getTime();
      const dateB = isWeekly ? new Date(b.weekStartDate).getTime() : new Date(b.date).getTime();
      return dateA - dateB;
    });

    const labels = sortedData.map(item =>
      isWeekly ? `Week ${item.weekNumber}, ${item.year}` : this.formatDate(item.date)
    );
    const playedData = sortedData.map(item => item.gamesPlayedCount);
    const wonData = sortedData.map(item => item.gamesWonCount);

    this.gamePlayChartData = {
      labels,
      datasets: [
        {
          label: 'Games Played',
          data: playedData,
          borderColor: '#cca374',
          backgroundColor: 'rgba(204, 163, 116, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Games Won',
          data: wonData,
          borderColor: '#00823d',
          backgroundColor: 'rgba(0, 130, 61, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  }

  generateRewardChart(): void {
    if (!this.rewardTrendData) return;

    // Check for weekly or daily data
    const weeklyData = (this.rewardTrendData as any).weeklyRewards;
    const dailyData = (this.rewardTrendData as any).dailyRewards;
    const isWeekly = !!weeklyData;
    const dataArray = weeklyData || dailyData;

    if (!dataArray) return;

    // Sort by date in ascending order
    const sortedData = [...dataArray].sort((a, b) => {
      const dateA = isWeekly ? new Date(a.weekStartDate).getTime() : new Date(a.date).getTime();
      const dateB = isWeekly ? new Date(b.weekStartDate).getTime() : new Date(b.date).getTime();
      return dateA - dateB;
    });

    const labels = sortedData.map(item =>
      isWeekly ? `Week ${item.weekNumber}, ${item.year}` : this.formatDate(item.date)
    );
    const data = sortedData.map(item => item.rewardCount);

    this.rewardChartData = {
      labels,
      datasets: [
        {
          label: 'Rewards Won',
          data,
          backgroundColor: '#00823d',
          borderColor: '#00823d',
          borderWidth: 1
        }
      ]
    };
  }

  onTimeRangeChange(range: TimeRange): void {
    this.selectedTimeRange = range;

    // Adjust date range based on selection
    this.endDate = new Date();
    this.startDate = new Date();

    if (range === 'daily') {
      this.startDate.setDate(this.startDate.getDate() - 30);
    } else {
      this.startDate.setDate(this.startDate.getDate() - 84); // 12 weeks
    }

    this.loadTrendData();
  }

  onTabChange(category: AnalyticsCategory): void {
    this.activeTab = category;
    this.updateCurrentChart();
  }

  updateCurrentChart(): void {
    switch (this.activeTab) {
      case 'registrations':
        this.currentChartData = { ...this.registrationChartData, datasets: [...this.registrationChartData.datasets] };
        this.currentChartOptions = this.lineChartOptions;
        this.currentChartType = 'line';
        if (this.registrationTrendData) {
          this.currentStats = [
            { label: 'Total Users', value: this.registrationTrendData.totalUsers.toLocaleString() },
            { label: 'Period', value: `${this.formatDate(this.registrationTrendData.startDate)} - ${this.formatDate(this.registrationTrendData.endDate)}` }
          ];
        }
        break;

      case 'marketing-emails':
        this.currentChartData = { ...this.marketingEmailChartData, datasets: [...this.marketingEmailChartData.datasets] };
        this.currentChartOptions = this.lineChartOptions;
        this.currentChartType = 'line';
        if (this.marketingEmailTrendData) {
          this.currentStats = [
            { label: 'Total Opt-Ins', value: this.marketingEmailTrendData.totalOptIns.toLocaleString() },
            { label: 'Opt-In Rate', value: this.formatPercentage(this.marketingEmailTrendData.optInPercentage) },
            { label: 'Period', value: `${this.formatDate(this.marketingEmailTrendData.startDate)} - ${this.formatDate(this.marketingEmailTrendData.endDate)}` }
          ];
        }
        break;

      case 'slots':
        this.currentChartData = { ...this.slotStatusChartData, datasets: [...this.slotStatusChartData.datasets] };
        this.currentChartOptions = this.barChartOptions;
        this.currentChartType = 'bar';
        if (this.slotStatusTrendData) {
          this.currentStats = [
            { label: 'Total Slots', value: this.slotStatusTrendData.totalSlots.toLocaleString() },
            { label: 'Period', value: `${this.formatDate(this.slotStatusTrendData.startDate)} - ${this.formatDate(this.slotStatusTrendData.endDate)}` }
          ];
        }
        break;

      case 'games':
        this.currentChartData = { ...this.gamePlayChartData, datasets: [...this.gamePlayChartData.datasets] };
        this.currentChartOptions = this.lineChartOptions;
        this.currentChartType = 'line';
        if (this.gamePlayTrendData) {
          this.currentStats = [
            { label: 'Total Played', value: this.gamePlayTrendData.totalGamesPlayed.toLocaleString() },
            { label: 'Total Won', value: this.gamePlayTrendData.totalGamesWon.toLocaleString() },
            { label: 'Win Rate', value: this.formatPercentage(this.gamePlayTrendData.winRate) }
          ];
        }
        break;

      case 'rewards':
        this.currentChartData = { ...this.rewardChartData, datasets: [...this.rewardChartData.datasets] };
        this.currentChartOptions = this.barChartOptions;
        this.currentChartType = 'bar';
        if (this.rewardTrendData) {
          this.currentStats = [
            { label: 'Total Rewards Won', value: this.rewardTrendData.totalRewardsWon.toLocaleString() },
            { label: 'Period', value: `${this.formatDate(this.rewardTrendData.startDate)} - ${this.formatDate(this.rewardTrendData.endDate)}` }
          ];
        }
        break;
    }
  }

  onDateRangeChange(start: Date, end: Date): void {
    this.startDate = start;
    this.endDate = end;
    this.loadTrendData();
  }

  refreshData(): void {
    this.loadAllSummaries();
    this.loadTrendData();
  }

  handleAuthError(error: any): void {
    if (error.status === 401 || error.status === 403) {
      this.authError = 'Invalid password. Please try again.';
      this.isAuthenticated = false;
      this.password = '';
    }
  }

  formatDate(date: Date | string | null): string {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  formatPercentage(value: number | null | undefined): string {
    if (value === null || value === undefined) return '0%';
    return `${value.toFixed(1)}%`;
  }
}
